import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Play, Plus, Trash2, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAddIssue, useDeleteIssue } from "@/hooks/use-room";
import type { Issue } from "@/lib/api/rooms";
import { applyServerErrors, type SetFieldError } from "@/lib/form-errors";
import { cn } from "@/lib/utils";

const schema = z.object({ title: z.string().trim().min(1).max(200) });

function StatusIcon({ status }: { status: Issue["status"] }) {
  if (status === "done") return <CheckCircle2 className="size-3.5 shrink-0 text-success" />;
  if (status === "voting") return <Play className="size-3.5 shrink-0 text-primary" />;
  return <div className="size-3.5 shrink-0 rounded-full border-2 border-muted-foreground/40" />;
}

export function IssueSidebar({
  roomId,
  issues,
  currentIssueId,
  canManage,
  onSelect,
  onClose,
}: {
  roomId: string;
  issues: Issue[];
  currentIssueId: string;
  canManage: boolean;
  onSelect: (issue: Issue) => void;
  onClose: () => void;
}) {
  const addIssue = useAddIssue(roomId);
  const deleteIssue = useDeleteIssue();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { title: "" },
  });

  const onSubmit = form.handleSubmit(async ({ title }) => {
    try {
      await addIssue.mutateAsync(title);
      form.reset();
    } catch (error) {
      const message = applyServerErrors(error, form.setError as SetFieldError);
      if (message) toast.error(message);
    }
  });

  return (
    <aside
      className="absolute inset-y-0 right-0 z-30 flex w-72 flex-col border-l border-border/50 bg-card sm:static sm:bg-card/50"
      aria-label="Issues"
      data-testid="issue-sidebar"
    >
      <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
        <span className="text-sm font-semibold">Issues</span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onClose}
          aria-label="Close issues"
        >
          <X />
        </Button>
      </div>

      <ul className="flex-1 space-y-1.5 overflow-y-auto p-3">
        {issues.map((issue) => (
          <li
            key={issue.id}
            data-testid="issue"
            className={cn(
              "group flex items-center gap-2 rounded-lg border border-transparent px-3 py-2 text-sm transition-colors",
              issue.id === currentIssueId
                ? "border-primary/30 bg-primary/10"
                : canManage && "hover:bg-secondary/50",
            )}
          >
            <StatusIcon status={issue.status} />
            {canManage ? (
              <button
                type="button"
                className="flex-1 cursor-pointer truncate text-left"
                onClick={() => onSelect(issue)}
                title="Vote on this issue"
              >
                {issue.title}
              </button>
            ) : (
              <span className="flex-1 truncate">{issue.title}</span>
            )}
            {issue.final_estimate && (
              <Badge variant="secondary" className="text-xs" data-testid="issue-estimate">
                {issue.final_estimate}
              </Badge>
            )}
            {canManage && (
              <Button
                variant="ghost"
                size="icon"
                className="size-6 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                onClick={() => deleteIssue.mutate(issue.id)}
                aria-label={`Delete ${issue.title}`}
              >
                <Trash2 className="size-3 text-destructive" />
              </Button>
            )}
          </li>
        ))}

        {issues.length === 0 && (
          <li className="py-6 text-center text-xs text-muted-foreground" data-testid="issues-empty">
            {canManage ? "No issues yet. Add one below." : "No issues yet."}
          </li>
        )}
      </ul>

      {canManage && (
        <Form {...form}>
          <form
            onSubmit={onSubmit}
            className="flex gap-2 border-t border-border/50 p-3"
            data-testid="issue-form"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input
                      placeholder="Add issue..."
                      aria-label="New issue"
                      className="h-8 text-sm"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button
              type="submit"
              size="icon"
              className="size-8 shrink-0"
              aria-label="Add issue"
              disabled={addIssue.isPending}
            >
              <Plus />
            </Button>
          </form>
        </Form>
      )}
    </aside>
  );
}
